import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import { Archive, Copy, MoreVertical, Pencil, Send, Trash2 } from 'lucide-react';
import {
  useDuplicateTaxonomy,
  useDeleteTaxonomy,
  useSetTaxonomyVersionStatus,
  type TaxonomySummary,
} from '@/entities/taxonomy';
import { ROUTES } from '@/shared/config/routes';
import { TaxonomyReasonModal } from '../TaxonomyReasonModal/TaxonomyReasonModal';

interface TaxonomyActionMenuProps {
  taxonomy: TaxonomySummary;
  /**
   * Whether the viewer may write. Duplicate, Publish, Archive and Delete are all
   * manage-guarded server-side, so without it they are absent rather than a
   * 403 on click; Edit stays, because it navigates to a read-only builder.
   */
  canManage: boolean;
}

type PendingAction = 'publish' | 'archive' | 'delete' | null;

/**
 * Row action menu for a taxonomy schema:
 *   - Edit      → navigates to the schema builder (a read; no modal, no reason)
 *   - Duplicate → creates a copy ("<name> Copy") via the duplicate endpoint
 *   - Publish   → visible when the head is a Draft
 *   - Archive   → visible when the head is Published
 *   - Delete    → always
 *
 * Publish / Archive / Delete are lifecycle changes and capture a mandatory
 * justification via {@link TaxonomyReasonModal} (backend contract); Duplicate
 * sends an implicit default reason.
 *
 * The trigger stops click propagation so opening the menu doesn't trigger the
 * surrounding row's navigation.
 */
export function TaxonomyActionMenu({ taxonomy, canManage }: TaxonomyActionMenuProps) {
  const { t } = useTranslation('knowledge');
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingAction>(null);

  const setStatus = useSetTaxonomyVersionStatus(taxonomy.id);
  const deleteTaxonomy = useDeleteTaxonomy(taxonomy.id);
  // Server-side duplicate: carries the head version's fields, rules, settings
  // and outputs. The old create-based copy produced an empty schema.
  const duplicate = useDuplicateTaxonomy();

  const handleDuplicate = () => {
    if (duplicate.isPending) return;
    duplicate.mutate(taxonomy.id);
  };

  const versionId = taxonomy.currentVersionId;
  const canPublish = taxonomy.status === 'draft' && !!versionId;
  const canArchive = taxonomy.status === 'published' && !!versionId;

  const isSubmitting = setStatus.isPending || deleteTaxonomy.isPending;

  const closeModal = () => setPending(null);

  const handleConfirm = (reason: string) => {
    if (pending === 'delete') {
      deleteTaxonomy.mutate({ reason }, { onSuccess: closeModal });
      return;
    }
    if ((pending === 'publish' || pending === 'archive') && versionId) {
      setStatus.mutate(
        { versionId, status: pending === 'publish' ? 'published' : 'archived', reason },
        { onSuccess: closeModal },
      );
    }
  };

  const modalConfig = {
    publish: {
      title: `Publish “${taxonomy.name}”`,
      description: t('taxonomy.taxonomyActionMenu.publishingMakesVersionActiveNaming'),
      confirmLabel: t('taxonomy.taxonomyActionMenu.publish'),
      destructive: false,
    },
    archive: {
      title: `Archive “${taxonomy.name}”`,
      description: t('taxonomy.taxonomyActionMenu.archivingRetiresSchemaActiveUse'),
      confirmLabel: t('taxonomy.taxonomyActionMenu.archive'),
      destructive: false,
    },
    delete: {
      title: `Delete “${taxonomy.name}”`,
      description: t('taxonomy.taxonomyActionMenu.removesSchemaListActionCannot'),
      confirmLabel: t('taxonomy.taxonomyActionMenu.delete'),
      destructive: true,
    },
  } as const;

  const active = pending ? modalConfig[pending] : null;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <span className="contents" onClick={(e) => e.stopPropagation()}>
      {/* React portals bubble events through the REACT tree, not the DOM tree,
          so a click inside the confirm dialog reaches the row's onClick and
          navigates. Catching it here keeps the whole menu subtree — dialogs
          included — from triggering row navigation. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="mini"
            aria-label={t('taxonomy.taxonomyActionMenu.taxonomyActions')}
            data-taxonomy-id={taxonomy.id}
            className="size-9 p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem
            onSelect={() => void navigate(ROUTES.KNOWLEDGE_HUB_TAXONOMY_DETAIL(taxonomy.id))}
          >
            <Pencil className="mr-2 size-4" />
            {t('taxonomy.taxonomyActionMenu.edit')}
          </DropdownMenuItem>
          {canManage && (
            <DropdownMenuItem onSelect={handleDuplicate} disabled={duplicate.isPending}>
              <Copy className="mr-2 size-4" />
              {t('taxonomy.taxonomyActionMenu.duplicate')}
            </DropdownMenuItem>
          )}
          {canManage && canPublish && (
            <DropdownMenuItem onSelect={() => setPending('publish')}>
              <Send className="mr-2 size-4" />
              {t('taxonomy.taxonomyActionMenu.publish')}
            </DropdownMenuItem>
          )}
          {canManage && canArchive && (
            <DropdownMenuItem onSelect={() => setPending('archive')}>
              <Archive className="mr-2 size-4" />
              {t('taxonomy.taxonomyActionMenu.archive')}
            </DropdownMenuItem>
          )}
          {canManage && (
            <DropdownMenuItem
              onSelect={() => setPending('delete')}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 size-4" />
              {t('taxonomy.taxonomyActionMenu.delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Stop row navigation from firing when interacting with the modal
          (Radix portals to body but React events bubble through the tree). */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={(e) => e.stopPropagation()}>
        <TaxonomyReasonModal
          open={active !== null}
          title={active?.title ?? ''}
          description={active?.description}
          confirmLabel={active?.confirmLabel ?? t('taxonomy.taxonomyActionMenu.confirm')}
          isSubmitting={isSubmitting}
          onCancel={closeModal}
          onConfirm={handleConfirm}
        />
      </div>
    </span>
  );
}

TaxonomyActionMenu.displayName = 'TaxonomyActionMenu';
